<script lang="ts" setup>
import type { Testable } from 'src/lib'
import type { Inputable } from 'src/lib/types/Inputable'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { AObjectSelectItem } from '../generics'
import Select from './Select.vue'
import SelectContent from './SelectContent.vue'
import SelectItem from './SelectItem.vue'
import SelectTrigger from './SelectTrigger.vue'
import SelectValue from './SelectValue.vue'

const { t } = useI18n({
  messages: {
    en: {
      'enter {item}': 'Enter {item}',
      'No option found.': 'No option found.',
    },
    nl: {
      'enter {item}': 'Voer {item} in',
      'No option found.': 'Geen optie gevonden.',
    },
  },
})

export interface ASelectProperties extends Testable, Inputable {
  items: AObjectSelectItem[]
  label?: string
  ariaLabel?: string
  placeholder?: string
  autocomplete?: boolean | string | undefined
  disabled?: boolean
}

const properties = defineProps<ASelectProperties>()

const computedAutocomplete = computed(() => {
  if (typeof properties.autocomplete === 'boolean') {
    return properties.autocomplete ? 'on' : 'off'
  }

  return properties.autocomplete
})
</script>

<template>
  <Select :id="id" :autocomplete="computedAutocomplete" :data-test>
    <div :class="properties.class">
      <SelectTrigger :aria-label="ariaLabel || label || placeholder">
        <SelectValue :placeholder="placeholder === undefined ? t('enter {item}', { item: label }) : placeholder" />
      </SelectTrigger>
    </div>

    <SelectContent>
      <slot v-if="items.length === 0" name="empty">
        <span class="flex w-full justify-center">
          {{ t('No option found.') }}
        </span>
      </slot>
      <template v-else>
        <SelectItem v-for="(item, index) in items" :key="index" :value="item.value">
          <slot name="item" v-bind="{ item, index }">
            {{ item.label }}
          </slot>
        </SelectItem>
      </template>
    </SelectContent>
  </Select>
</template>
